import { useEffect, useState, useRef } from 'react';
import { X, Star, Heart, Eye, Clock, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TheaterOverlay = ({ content, onClose }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [contentData, setContentData] = useState(content);
  const ruffleRef = useRef(null);

  useEffect(() => {
    // Prevent body scroll when overlay is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    // Handle escape key
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    // Initialize Ruffle for Flash games
    if (content.content_type === 'game' && ruffleRef.current) {
      const loadRuffle = async () => {
        try {
          // Load Ruffle from CDN
          if (!window.RufflePlayer) {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/@aspect/ruffle-player@latest/dist/ruffle.js';
            script.async = true;
            document.head.appendChild(script);
            
            await new Promise((resolve) => {
              script.onload = resolve;
            });
          }

          const ruffle = window.RufflePlayer.newest();
          const player = ruffle.createPlayer();
          player.style.width = '100%';
          player.style.height = '100%';
          ruffleRef.current.innerHTML = '';
          ruffleRef.current.appendChild(player);
          
          const fileUrl = content.file_url.startsWith('/') 
            ? `${process.env.REACT_APP_BACKEND_URL}${content.file_url}`
            : content.file_url;
          
          await player.load(fileUrl);
        } catch (error) {
          console.error('Ruffle error:', error);
          toast.error('Failed to load Flash content');
        }
      };

      loadRuffle();
    }
  }, [content]);

  const handleVote = async (score) => {
    if (!user) {
      toast.error('Login to vote');
      return;
    }

    try {
      const response = await fetch(`${API}/content/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content_id: content.content_id, score })
      });

      if (response.ok) {
        const data = await response.json();
        setRating(score);
        setContentData(prev => ({ ...prev, average_rating: data.new_average }));
        toast.success(`Voted ${score}/5`);
      }
    } catch (error) {
      toast.error('Vote failed');
    }
  };

  const handleFavorite = async () => {
    if (!user) {
      toast.error('Login to favorite');
      return;
    }

    try {
      const response = await fetch(`${API}/user/favorite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content_id: content.content_id })
      });

      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.favorited);
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getTimeRemaining = () => {
    if (!contentData.is_under_judgment) return null;
    const end = new Date(contentData.judgment_end_time);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return 'Judgment ended';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m remaining`;
  };

  const getBlamStatus = () => {
    const avg = contentData.average_rating;
    if (avg >= 3.5) return { color: 'safe', label: 'PROTECTED' };
    if (avg >= 2.0) return { color: 'warning', label: 'SURVIVING' };
    return { color: 'danger', label: 'DANGER ZONE' };
  };

  return (
    <div 
      className="theater-overlay" 
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="theater-overlay"
    >
      <div className="relative w-full max-w-5xl mx-4 bg-[#0a0a0a] border border-[#333]">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-2 bg-[#1a1a1a] border border-[#333] hover:border-[#cc0000] transition-colors"
          data-testid="theater-close-btn"
        >
          <X size={20} className="text-[#999]" />
        </button>

        {/* Content area */}
        <div className="aspect-video bg-black">
          {content.content_type === 'video' ? (
            <video
              src={content.file_url.startsWith('/') 
                ? `${process.env.REACT_APP_BACKEND_URL}${content.file_url}`
                : content.file_url}
              controls
              autoPlay
              className="w-full h-full"
              data-testid="theater-video"
            />
          ) : (
            <div 
              ref={ruffleRef} 
              className="w-full h-full flex items-center justify-center"
              data-testid="theater-game"
            >
              <p className="text-[#666] mono">Loading Flash content...</p>
            </div>
          )}
        </div>

        {/* Info panel */}
        <div className="p-4 border-t border-[#222]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-[200px]">
              <h2 className="text-xl font-bold text-white mb-1" data-testid="theater-title">
                {contentData.title}
              </h2>
              <p className="text-sm text-[#999] mb-2" data-testid="theater-author">
                by <span className="text-[#ffcc00]">{contentData.author_name}</span>
              </p>
              <p className="text-xs text-[#666] line-clamp-2">
                {contentData.description}
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-xs mono">
              <div className="flex items-center gap-1 text-[#999]">
                <Eye size={14} />
                <span data-testid="theater-views">{contentData.view_count}</span>
              </div>
              <div className="flex items-center gap-1 text-[#999]">
                <Clock size={14} />
                <span>{formatDate(contentData.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Judgment status */}
          {contentData.is_under_judgment && (
            <div className="mt-4 p-3 bg-[#111] border border-[#333]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#999] uppercase">Under Judgment</span>
                <span className={`text-xs mono ${getBlamStatus().color === 'danger' ? 'text-[#ff0000]' : getBlamStatus().color === 'warning' ? 'text-[#ffcc00]' : 'text-[#44ff44]'}`}>
                  {getBlamStatus().label}
                </span>
              </div>
              <div className="ws-blam-meter mb-2">
                <div 
                  className={`ws-blam-fill ${getBlamStatus().color}`}
                  style={{ width: `${Math.min(100, (contentData.average_rating / 5) * 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#666]">
                  {contentData.total_votes} votes • {contentData.average_rating.toFixed(2)} avg
                </span>
                <span className="text-[#cc0000] mono">
                  {getTimeRemaining()}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            {/* Rating */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#666] uppercase">Vote:</span>
              <div className="flex gap-1" data-testid="theater-rating">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    onClick={() => handleVote(score)}
                    onMouseEnter={() => setHoverRating(score)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-colors"
                    data-testid={`rating-${score}`}
                  >
                    <div className={`w-6 h-6 border transition-all ${
                      (hoverRating || rating) >= score 
                        ? 'bg-[#cc0000] border-[#ff3333] shadow-[0_0_5px_rgba(204,0,0,0.5)]'
                        : 'bg-[#222] border-[#333]'
                    }`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Favorite */}
            <button
              onClick={handleFavorite}
              className={`flex items-center gap-1 px-3 py-1.5 border transition-colors ${
                isFavorited 
                  ? 'bg-[#cc0000] border-[#990000] text-white'
                  : 'bg-[#1a1a1a] border-[#333] text-[#999] hover:border-[#cc0000]'
              }`}
              data-testid="theater-favorite-btn"
            >
              <Heart size={14} fill={isFavorited ? 'currentColor' : 'none'} />
              <span className="text-xs uppercase">{isFavorited ? 'Favorited' : 'Favorite'}</span>
            </button>

            {/* Current rating display */}
            <div className="ml-auto text-right">
              <div className="text-2xl font-bold text-[#cc0000] mono" data-testid="theater-avg-rating">
                {contentData.average_rating.toFixed(2)}
              </div>
              <div className="text-xs text-[#666]">/ 5.00</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
